import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Poi } from '../../../classes/common/poi';
import { ViewService } from '../../../services/common/view.service';

/** Indirizzo suggerito da LocationIQ: coordinate + indirizzo completo (display_name). */
export interface AddressSuggestion {
  lat: number;
  lng: number;
  display_name: string;
}

export interface AddressSearchResult {
  /** Lista di indirizzi suggeriti (max 5). Vuota se nessun risultato. */
  suggestions?: Array<{ lat: any; lon: any; display_name: string }>;
  /** Query corrispondente. Serve a evitare di mostrare risultati per ricerche stale. */
  query?: string;
  /** Errore opzionale (per ora non visualizzato). */
  error?: any;
}

const STORAGE_KEY = 'addressSearch.recents';
const MAX_RECENTS = 8;

@Component({
  selector: 'app-address-search',
  templateUrl: './address-search.component.html',
  styleUrls: ['./address-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressSearchComponent implements OnInit, OnChanges {

  @Input() query: string = '';
  @Output() queryChange = new EventEmitter<string>();

  @Input() searching: boolean = false;
  /** Submit della ricerca. `advanced` true = modalità "indirizzo + aree POI":
   *  il parent in questo caso NON deve disegnare il marker blu di creazione POI. */
  @Output() search = new EventEmitter<{ query: string; advanced: boolean }>();

  /** Lista completa dei POI dell'organization corrente. */
  @Input() pois: Poi[] = [];

  /** Risultato della geocodifica fornito dal parent. Quando cambia in modalità
   *  avanzata, il componente calcola i POI di appartenenza. */
  @Input() searchResult: AddressSearchResult | null = null;

  /** Click su un POI di appartenenza: il parent seleziona/fitta il POI e mostra anche
   *  l'indirizzo cercato (marker alle coords) dentro l'area. */
  @Output() poiSelect = new EventEmitter<{ poi: Poi; coords: { lat: number; lng: number } | null }>();
  /** Click sulla stella di un POI di appartenenza: toggle visibilità mappa, gestito dal parent. */
  @Output() poiToggle = new EventEmitter<Poi>();
  /** Click su un indirizzo della lista (tab "solo indirizzo"): il parent centra la mappa e disegna il marker. */
  @Output() addressSelect = new EventEmitter<{ query: string; coords: { lat: number; lng: number } }>();

  /** Lista di indirizzi suggeriti (tab "solo indirizzo"). [] = nessuna ricerca/risultato. */
  suggestions: AddressSuggestion[] = [];
  /** Indirizzo selezionato dall'utente (per evidenziarlo e calcolare i POI di appartenenza). */
  selectedSuggestion: AddressSuggestion | null = null;

  @ViewChild('inputEl') inputEl: ElementRef<HTMLInputElement>;

  open = false;
  recents: string[] = [];

  /** In attesa che il parent ritorni le coordinate dopo l'Invio. */
  awaitingResult = false;

  /** La ricerca è stata rifiutata perché siamo in demo: mostro il warning nel
   *  pannello invece della lista risultati. */
  demoBlocked = false;

  /** POI che contengono l'indirizzo cercato (modalità avanzata). null = nessuna ricerca avanzata in corso. */
  matchedPois: Poi[] | null = null;

  /** Query con cui è stato fatto l'ultimo submit avanzato (per ignorare risultati stale). */
  private pendingQuery: string | null = null;

  constructor(
    private cd: ChangeDetectorRef,
    public view: ViewService,
  ) {}

  ngOnInit() {
    this.loadRecents();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes['searchResult'] && this.awaitingResult) {
      this.handleSearchResult();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent) {
    if (!this.open) return;
    if (this.matchedPois !== null || this.suggestions.length > 0 || this.awaitingResult) return;
    const el = (this.inputEl?.nativeElement?.closest('.as-wrap') as HTMLElement);
    if (el && !el.contains(ev.target as Node)) this.open = false;
  }

  onInput(value: string) {
    this.query = value || '';
    this.queryChange.emit(this.query);
    // Nuova digitazione: invalido i risultati e richiudo il popover (riappare al Cerca).
    this.resetResults();
    this.awaitingResult = false;
    this.pendingQuery = null;
    this.open = false;
  }

  /** Il focus sulla barra NON apre il popover: tab/risultati appaiono solo dopo il Cerca. */
  onFocus() {
  }

  /** X: chiude tutto (popover, tab, risultati) e lascia solo la barra di ricerca vuota. */
  clear() {
    this.query = '';
    this.queryChange.emit('');
    this.resetResults();
    this.awaitingResult = false;
    this.pendingQuery = null;
    this.open = false;
    // Tolgo il focus: un re-focus riaprirebbe il popover.
    this.inputEl?.nativeElement?.blur();
  }

  /** Avvia la ricerca (bottone "Cerca" o Invio): recupera la lista di indirizzi
   *  suggeriti (tab 1). I POI (tab 2) si calcolano alla scelta di un indirizzo. */
  submit() {
    const q = (this.query || '').trim();
    if (!q || this.searching) return;
    this.pushRecent(q);
    this.resetResults();
    this.demoBlocked = false;
    this.awaitingResult = true;
    this.pendingQuery = q;
    this.open = true;
    this.search.emit({ query: q, advanced: false });
  }

  /** Azzera i risultati delle ricerca (suggerimenti indirizzo + POI + selezione). */
  private resetResults() {
    this.suggestions = [];
    this.selectedSuggestion = null;
    this.matchedPois = null;
  }

  /** Click su un indirizzo suggerito: centro/marker via parent + POI di appartenenza. */
  selectSuggestion(s: AddressSuggestion) {
    if (!s) return;
    this.selectedSuggestion = s;
    this.addressSelect.emit({ query: s.display_name, coords: { lat: s.lat, lng: s.lng } });
    this.matchedPois = this.computeMatchedPois(s);
    this.cd.markForCheck();
  }

  /** Click su un POI proposto: lo seleziono e passo le coords dell'indirizzo cercato
   *  così il parent mostra anche il marker dell'indirizzo dentro l'area. */
  selectMatchedPoi(poi: Poi) {
    const coords = this.selectedSuggestion
      ? { lat: this.selectedSuggestion.lat, lng: this.selectedSuggestion.lng }
      : null;
    this.poiSelect.emit({ poi, coords });
  }

  /** POI che contengono l'indirizzo dato (da Poi.containsPoint). */
  private computeMatchedPois(s: AddressSuggestion): Poi[] {
    const coords = { lat: s.lat, lng: s.lng };
    return (this.pois || []).filter(p => p && p.containsPoint(coords));
  }

  pickRecent(s: string) {
    this.query = s;
    this.queryChange.emit(s);
    this.submit();
  }

  removeRecent(ev: Event, s: string) {
    ev.stopPropagation();
    this.recents = this.recents.filter(x => x !== s);
    this.persistRecents();
  }

  /** Riceve la lista di indirizzi suggeriti dal parent e popola il tab "solo indirizzo". */
  private handleSearchResult() {
    // Risultato stale (parent ha geocodificato per una query diversa o vecchia).
    // Confronto trim-tollerante: evita di restare bloccati in awaitingResult per
    // semplici differenze di spazi tra la query emessa e quella rimandata dal parent.
    if (this.pendingQuery && this.searchResult?.query
        && this.searchResult.query.trim() !== this.pendingQuery.trim()) {
      return;
    }
    this.awaitingResult = false;
    this.pendingQuery = null;
    this.selectedSuggestion = null;
    this.matchedPois = null;

    /* Demo: nessun risultato ma il pannello resta aperto a spiegare perché. */
    if (this.searchResult?.error === 'demo') {
      this.demoBlocked = true;
      this.suggestions = [];
      this.open = true;
      return;
    }

    const raw = this.searchResult?.suggestions || [];
    // LocationIQ ritorna lat/lon come stringhe: le converto in numeri.
    this.suggestions = raw
      .map(s => ({ lat: parseFloat(s.lat), lng: parseFloat(s.lon), display_name: s.display_name }))
      .filter(s => !isNaN(s.lat) && !isNaN(s.lng));

    if (this.suggestions.length === 0) {
      // Nessun indirizzo trovato: chiudo il popover (il parent mostra già l'alert).
      this.open = false;
    } else {
      /* Pre-seleziono il primo indirizzo e calcolo subito i POI di appartenenza,
       * così la sezione "rientra in N aree P.O.I." compare già sotto la lista
       * senza dover cliccare. NON centro la mappa qui: il centraggio + marker
       * avvengono solo al click su un indirizzo (selectSuggestion). */
      this.selectedSuggestion = this.suggestions[0];
      this.matchedPois = this.computeMatchedPois(this.suggestions[0]);
    }
    this.cd.markForCheck();
  }

  private loadRecents() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      this.recents = raw ? JSON.parse(raw) : [];
    } catch { this.recents = []; }
  }

  private persistRecents() {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.recents)); } catch {}
  }

  private pushRecent(s: string) {
    this.recents = [s, ...this.recents.filter(x => x !== s)].slice(0, MAX_RECENTS);
    this.persistRecents();
  }
}
